import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { Radio, Search, MessageSquare, ArrowRight, Filter, Sparkles } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type Msg = { id: number; investorId: number; investor: { id: number; name: string; firm: string | null } | null; role: string; content: string; createdAt: string };
type Investor = { id: number; name: string; firm: string | null };

export default function BankerInvestorSignals() {
  const [data, setData] = useState<{ investors: Investor[]; messages: Msg[] }>({ investors: [], messages: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [investorFilter, setInvestorFilter] = useState<number | "all">("all");
  const [roleFilter, setRoleFilter] = useState<"all" | "user" | "assistant">("user");

  useEffect(() => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    fetch(`${BASE}api/banker/investor-signals?limit=120`, { headers: { "x-banker-token": token } })
      .then(r => r.ok ? r.json() : { investors: [], messages: [] })
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => data.messages.filter(m => {
    if (investorFilter !== "all" && m.investorId !== investorFilter) return false;
    if (roleFilter !== "all" && m.role !== roleFilter) return false;
    if (q && !m.content.toLowerCase().includes(q.toLowerCase()) && !(m.investor?.name || "").toLowerCase().includes(q.toLowerCase())) return false;
    if (!m.content) return false;
    return true;
  }), [data.messages, investorFilter, roleFilter, q]);

  // Quick stats per investor
  const perInvestor = useMemo(() => {
    const map = new Map<number, { investor: Investor; total: number; lastAt: string }>();
    data.messages.forEach(m => {
      if (!m.investor || m.role !== "user") return;
      const cur = map.get(m.investorId);
      if (!cur) map.set(m.investorId, { investor: m.investor, total: 1, lastAt: m.createdAt });
      else { cur.total++; if (new Date(m.createdAt) > new Date(cur.lastAt)) cur.lastAt = m.createdAt; }
    });
    return Array.from(map.values()).sort((a, b) => new Date(b.lastAt).getTime() - new Date(a.lastAt).getTime());
  }, [data.messages]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Radio className="w-7 h-7 text-[#FF5C00]" /> Investor Signals</h1>
        <p className="text-white/50 text-sm mt-1">
          Every question your investors are asking Kavya — including the generic ones that never became formal Meera tasks. Skim it like a feed.
        </p>
      </div>

      {/* Most-active strip */}
      {perInvestor.length > 0 && (
        <div>
          <p className="text-white/40 text-[11px] uppercase tracking-wider font-mono mb-2 flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Most active this period</p>
          <div className="flex gap-2 flex-wrap">
            {perInvestor.slice(0, 8).map(({ investor, total, lastAt }) => {
              const active = investorFilter === investor.id;
              return (
                <button key={investor.id} onClick={() => setInvestorFilter(active ? "all" : investor.id)}
                  className={`text-left rounded-xl border px-3.5 py-2.5 transition-all ${active ? "bg-[#FF5C00]/10 border-[#FF5C00]/50" : "bg-white/[0.04] border-white/[0.08] hover:border-white/[0.2]"}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-[#FF5C00]/15 border border-[#FF5C00]/25 flex items-center justify-center text-[10px] font-bold text-[#FF5C00]">{investor.name.charAt(0)}</div>
                    <div>
                      <p className="text-white text-xs font-medium leading-tight">{investor.name}</p>
                      <p className="text-white/40 text-[10px] font-mono">{total} ask{total === 1 ? "" : "s"} · {new Date(lastAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions, names, or firms..."
            className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/40" />
        </div>
        <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.1] rounded-lg p-0.5 text-xs">
          <Filter className="w-3.5 h-3.5 text-white/40 ml-1.5" />
          <button onClick={() => setRoleFilter("user")} className={`px-3 py-1.5 rounded ${roleFilter === "user" ? "bg-[#FF5C00] text-white" : "text-white/60"}`}>Investor asks</button>
          <button onClick={() => setRoleFilter("assistant")} className={`px-3 py-1.5 rounded ${roleFilter === "assistant" ? "bg-[#FF5C00] text-white" : "text-white/60"}`}>Kavya replies</button>
          <button onClick={() => setRoleFilter("all")} className={`px-3 py-1.5 rounded ${roleFilter === "all" ? "bg-[#FF5C00] text-white" : "text-white/60"}`}>All</button>
        </div>
        {investorFilter !== "all" && (
          <button onClick={() => setInvestorFilter("all")} className="text-xs text-white/50 hover:text-white border border-white/15 rounded-lg px-3 py-1.5">Clear investor filter</button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-12 text-center">
          <MessageSquare className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm font-medium">
            {data.messages.length === 0
              ? "No investor chatter yet."
              : "Nothing matches that filter."}
          </p>
          <p className="text-white/40 text-xs mt-1.5">
            {data.messages.length === 0
              ? "When your investors talk to Kavya, every message lands here."
              : "Try clearing filters or broadening your search."}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(m => (
            <div key={m.id} className={`rounded-xl p-4 border ${m.role === "user" ? "bg-[#FF5C00]/[0.04] border-[#FF5C00]/15" : "bg-white/[0.03] border-white/[0.08]"}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold ${m.role === "user" ? "bg-[#FF5C00]/15 border border-[#FF5C00]/25 text-[#FF5C00]" : "bg-white/10 border border-white/15 text-white/70"}`}>
                    {m.role === "user" ? (m.investor?.name.charAt(0) || "?") : "K"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {m.role === "user" ? (m.investor?.name || "Investor") : `Kavya → ${m.investor?.name || "Investor"}`}
                    </p>
                    {m.investor?.firm && <p className="text-white/40 text-[10px] font-mono truncate">{m.investor.firm}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-white/40 text-[10px] font-mono whitespace-nowrap">{new Date(m.createdAt).toLocaleString()}</span>
                  {m.investor && (
                    <Link href={`/banker/investors`}>
                      <a className="text-white/40 hover:text-[#FF5C00]" title="Open in My Investors"><ArrowRight className="w-3.5 h-3.5" /></a>
                    </Link>
                  )}
                </div>
              </div>
              <p className="text-white/85 text-sm leading-relaxed whitespace-pre-wrap line-clamp-6">{m.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
